import { useMemo, useState } from "react";
import { Flame } from "lucide-react";

const WEEK_OPTIONS = [12, 52];
const DAY_MS = 24 * 60 * 60 * 1000;

function startOfDay(d) {
  const dt = new Date(d);
  dt.setHours(0, 0, 0, 0);
  return dt;
}

function intensityClass(count) {
  if (count === 0) return "bg-[#141414] border-zinc-900";
  if (count === 1) return "bg-red-950 border-red-900";
  if (count === 2) return "bg-red-800 border-red-700";
  if (count === 3) return "bg-red-700 border-red-600";
  return "bg-red-600 border-red-500";
}

/**
 * Renders a GitHub-style heatmap of daily activity.
 * Activity score = (journal entries that day) + (past events whose date is that day).
 */
export default function ActivityHeatmap({ notes, events }) {
  const [weeks, setWeeks] = useState(12);

  const { matrix, totals, currentStreak, longestStreak, weekLabels } = useMemo(() => {
    const counts = new Map();
    for (const n of notes || []) {
      if (!n.date) continue;
      counts.set(n.date, (counts.get(n.date) || 0) + 1);
    }
    const today = startOfDay(new Date());
    for (const e of events || []) {
      if (!e.date) continue;
      const evDate = startOfDay(new Date(e.date));
      if (evDate <= today) {
        counts.set(e.date, (counts.get(e.date) || 0) + 1);
      }
    }

    // Build columns of 7 days, ending today. Find Sunday of current week.
    const todayDow = today.getDay(); // 0=Sun
    const endOfRange = new Date(today.getTime() + (6 - todayDow) * DAY_MS); // upcoming Sat
    const startOfRange = new Date(endOfRange.getTime() - (weeks * 7 - 1) * DAY_MS);

    const cols = [];
    const labels = [];
    let cursor = new Date(startOfRange);
    while (cursor <= endOfRange) {
      const week = [];
      const monthAtStart = cursor.getMonth();
      labels.push(cursor.getDate() <= 7 ? cursor.toLocaleString("en-US", { month: "short" }) : "");
      for (let i = 0; i < 7; i++) {
        const iso = cursor.toISOString().slice(0, 10);
        const inFuture = cursor > today;
        week.push({ date: iso, count: counts.get(iso) || 0, future: inFuture });
        cursor = new Date(cursor.getTime() + DAY_MS);
      }
      cols.push(week);
      void monthAtStart;
    }

    // Streak math (consecutive days with count > 0, counting back from today)
    let cs = 0;
    let cursorStreak = new Date(today);
    while (true) {
      const iso = cursorStreak.toISOString().slice(0, 10);
      if ((counts.get(iso) || 0) > 0) {
        cs += 1;
        cursorStreak = new Date(cursorStreak.getTime() - DAY_MS);
      } else {
        break;
      }
    }

    // Longest streak within range
    let ls = 0;
    let run = 0;
    const allDates = [...cols.flat()].sort((a, b) => a.date.localeCompare(b.date));
    for (const cell of allDates) {
      if (cell.count > 0 && !cell.future) {
        run += 1;
        if (run > ls) ls = run;
      } else {
        run = 0;
      }
    }

    const total = [...counts.values()].reduce((a, b) => a + b, 0);
    return { matrix: cols, totals: total, currentStreak: cs, longestStreak: ls, weekLabels: labels };
  }, [notes, events, weeks]);

  return (
    <section className="tactical-card p-6" data-testid="activity-heatmap-section">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
        <div>
          <p className="font-label text-red-600">// 04</p>
          <h2 className="font-heading text-3xl mt-1 flex items-center gap-2">
            <Flame className="w-6 h-6" strokeWidth={2.5} /> Activity Heatmap
          </h2>
          <p className="text-zinc-500 text-sm mt-1">
            Daily training intensity — journal entries + sessions attended.
          </p>
        </div>
        <div className="flex items-center gap-2" data-testid="heatmap-range-toggle">
          {WEEK_OPTIONS.map((w) => (
            <button
              key={w}
              onClick={() => setWeeks(w)}
              data-testid={`heatmap-range-${w}`}
              className={`px-3 py-2 text-xs font-heading tracking-wider border transition-colors ${
                weeks === w
                  ? "border-red-600 bg-red-600 text-white"
                  : "border-zinc-800 text-zinc-400 hover:border-zinc-600"
              }`}
            >
              {w}W
            </button>
          ))}
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <Stat label="Total Sessions" value={totals} testid="heatmap-total" />
        <Stat label="Current Streak" value={`${currentStreak} ${currentStreak === 1 ? "day" : "days"}`} testid="heatmap-current-streak" />
        <Stat label="Best Streak" value={`${longestStreak} ${longestStreak === 1 ? "day" : "days"}`} testid="heatmap-longest-streak" />
      </div>

      {/* Heatmap grid */}
      <div className="overflow-x-auto pb-2" data-testid="heatmap-grid">
        <div className="flex gap-1 min-w-fit">
          {matrix.map((week, ci) => (
            <div key={week[0].date} className="flex flex-col gap-1">
              <span className="font-mono text-[9px] text-zinc-500 h-3 leading-3">
                {weekLabels[ci]}
              </span>
              {week.map((cell) => (
                <div
                  key={cell.date}
                  title={`${cell.date} — ${cell.count} ${cell.count === 1 ? "entry" : "entries"}`}
                  className={`${
                    weeks === 52 ? "w-2.5 h-2.5" : "w-4 h-4"
                  } border ${cell.future ? "bg-transparent border-dashed border-zinc-900" : intensityClass(cell.count)}`}
                  data-testid={`heatmap-cell-${cell.date}`}
                />
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="mt-4 flex items-center gap-2 text-[10px] font-mono text-zinc-500">
        <span>LESS</span>
        {[0, 1, 2, 3, 4].map((n) => (
          <div key={n} className={`w-3 h-3 border ${intensityClass(n)}`} />
        ))}
        <span>MORE</span>
      </div>
    </section>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="border border-zinc-800 bg-[#0A0A0A] p-3" data-testid={testid}>
      <p className="font-label text-[10px]">{label}</p>
      <p className="font-heading text-2xl mt-1">{value}</p>
    </div>
  );
}
